import { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, X, Upload, Loader2, Copy, ExternalLink } from "lucide-react";
import { resolveImageUrl } from "../lib/imageUrl";
import AdminGallery from "../components/AdminGallery";
import AdminCustomers from "../components/AdminCustomers";
import AdminCoupons from "../components/AdminCoupons";

const ORDER_STATUSES = ["pending", "confirmed", "packed", "out_for_delivery", "delivered", "cancelled"];
const STATUS_LABELS = {
  pending: "Pending",
  confirmed: "Confirmed",
  packed: "Packed",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  cancelled: "Cancelled",
};
const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-blue-100 text-blue-800",
  packed: "bg-indigo-100 text-indigo-800",
  out_for_delivery: "bg-purple-100 text-purple-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

const emptyProduct = {
  name_en: "",
  name_te: "",
  description_en: "",
  description_te: "",
  price_per_kg: 0,
  image_url: "",
  category: "rice",
  pack_sizes: [5, 10, 26, 50],
  price_tiers: [],
  stock_kg: "",
  is_active: true,
};
const CATEGORY_OPTIONS = ["rice", "pulses", "seeds", "oil_cakes", "fertilizer", "jute_bags", "by_product", "other"];

export default function AdminDashboard() {
  const { t, admin, logout, bootChecked } = useApp();
  const [tab, setTab] = useState("orders");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null); // product object or null
  const [form, setForm] = useState(emptyProduct);

  const loadData = async () => {
    try {
      const [p, o] = await Promise.all([
        api.get("/products?include_inactive=true"),
        api.get("/orders"),
      ]);
      setProducts(p.data);
      setOrders(o.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load dashboard data");
    }
  };

  useEffect(() => {
    if (admin) loadData();
  }, [admin]);

  if (!bootChecked) return <div className="p-10 text-stone-500">{t.common.loading}</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const openNew = () => {
    setEditing("new");
    setForm(emptyProduct);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p });
  };
  const closeModal = () => {
    setEditing(null);
    setForm(emptyProduct);
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    try {
      // Parse comma-separated pack sizes string → array of positive numbers
      const sizesRaw = Array.isArray(form.pack_sizes) ? form.pack_sizes.join(",") : String(form.pack_sizes || "");
      const sizes = sizesRaw
        .split(",")
        .map((s) => parseFloat(s.trim()))
        .filter((n) => !isNaN(n) && n > 0);
      if (sizes.length === 0) {
        toast.error("Add at least one pack size (e.g. 5, 10, 26, 50)");
        return;
      }
      // Parse comma-separated bulk tiers — format: "5:60, 10:58, 50:52"
      const tiersRaw = Array.isArray(form.price_tiers)
        ? form.price_tiers.map((t) => `${t.pack_kg}:${t.price_per_kg}`).join(", ")
        : String(form.price_tiers || "");
      const tiers = tiersRaw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => {
          const [pk, pr] = s.split(":").map((x) => parseFloat(x));
          return !isNaN(pk) && !isNaN(pr) && pk > 0 && pr > 0 ? { pack_kg: pk, price_per_kg: pr } : null;
        })
        .filter(Boolean);
      const payload = {
        ...form,
        price_per_kg: Number(form.price_per_kg),
        pack_sizes: sizes,
        price_tiers: tiers,
        stock_kg: form.stock_kg === "" || form.stock_kg === null ? null : Number(form.stock_kg),
      };
      if (editing === "new") {
        await api.post("/products", payload);
        toast.success("Product added");
      } else {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      }
      closeModal();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const deleteProduct = async (id) => {
    if (!confirm("Delete this product?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Deleted");
      loadData();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="bg-[#fbf8f1] min-h-[90vh]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] tracking-[0.2em] uppercase text-[#b45f06] font-semibold">
              {admin.email}
            </div>
            <h1 className="text-3xl font-semibold text-stone-900 mt-1">{t.admin.dashboard}</h1>
          </div>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="inline-flex items-center gap-2 px-4 py-2 border border-stone-300 rounded-sm text-sm hover:bg-white"
          >
            <LogOut size={14} /> {t.admin.logout}
          </button>
        </div>

        <div className="mt-8 border-b border-stone-200 flex gap-6 overflow-x-auto">
          {[
            { key: "orders", label: t.admin.orders, count: orders.length },
            { key: "customers", label: "Customers", count: null },
            { key: "products", label: t.admin.products, count: products.length },
            { key: "coupons", label: "Coupons", count: null },
            { key: "gallery", label: "Gallery", count: null },
          ].map((k) => (
            <button
              key={k.key}
              data-testid={`tab-${k.key}`}
              onClick={() => setTab(k.key)}
              className={`pb-3 text-sm font-semibold tracking-wide transition-colors whitespace-nowrap ${
                tab === k.key ? "text-[#274e13] border-b-2 border-[#274e13]" : "text-stone-500"
              }`}
            >
              {k.label}
              {k.count !== null && ` (${k.count})`}
            </button>
          ))}
        </div>

        {tab === "customers" && <AdminCustomers />}
        {tab === "coupons" && <AdminCoupons />}
        {tab === "gallery" && <AdminGallery />}

        {tab === "orders" && (
          <div className="mt-8">
            {orders.length === 0 ? (
              <div className="text-stone-500">{t.admin.noOrders}</div>
            ) : (
              <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-stone-100">
                    <tr className="text-left text-xs tracking-wider uppercase text-stone-600">
                      <th className="px-4 py-3">#Tracking</th>
                      <th className="px-4 py-3">{t.admin.customer}</th>
                      <th className="px-4 py-3">{t.admin.amount}</th>
                      <th className="px-4 py-3">{t.admin.status}</th>
                      <th className="px-4 py-3">Delivery link</th>
                      <th className="px-4 py-3">{t.admin.date}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => (
                      <OrderRow key={o.id} order={o} onUpdated={loadData} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "products" && (
          <div className="mt-8">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <button
                data-testid="add-product-btn"
                onClick={openNew}
                className="inline-flex items-center gap-2 bg-[#274e13] hover:bg-[#38761d] text-white px-4 py-2 rounded-sm text-sm font-semibold transition-colors"
              >
                <Plus size={14} /> {t.common.addProduct}
              </button>
              <div className="text-xs text-stone-500">
                Tip: Set <span className="font-semibold">price + image</span> and it goes live instantly. Uncheck "In stock" only if temporarily unavailable.
              </div>
            </div>

            <div className="mt-6 bg-white border border-stone-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-100">
                  <tr className="text-left text-xs tracking-wider uppercase text-stone-600">
                    <th className="px-4 py-3">Product</th>
                    <th className="px-4 py-3">Price/kg</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => {
                    const isDraft = !p.is_active || !p.price_per_kg || !p.image_url;
                    return (
                      <tr key={p.id} className="border-t border-stone-100" data-testid={`product-row-${p.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-sm bg-stone-100 overflow-hidden shrink-0">
                              {p.image_url ? (
                                <img src={resolveImageUrl(p.image_url)} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full grid place-items-center text-[9px] text-stone-400 uppercase">none</div>
                              )}
                            </div>
                            <div>
                              <div className="font-semibold text-stone-900">{p.name_en}</div>
                              <div className="text-xs text-stone-500 font-telugu">{p.name_te}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {p.price_per_kg > 0 ? (
                            `₹${p.price_per_kg.toFixed(0)}`
                          ) : (
                            <span className="text-amber-700 text-xs">Set price</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isDraft ? (
                            <span className="inline-block text-[10px] tracking-wider uppercase font-semibold px-2 py-1 rounded-sm bg-amber-100 text-amber-800">
                              Draft
                            </span>
                          ) : (
                            <span className="inline-block text-[10px] tracking-wider uppercase font-semibold px-2 py-1 rounded-sm bg-green-100 text-green-800">
                              Live
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              data-testid={`edit-product-${p.id}`}
                              onClick={() => openEdit(p)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#274e13] hover:bg-stone-100 rounded-sm border border-stone-300"
                            >
                              <Pencil size={12} /> Edit
                            </button>
                            <button
                              data-testid={`delete-product-${p.id}`}
                              onClick={() => deleteProduct(p.id)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 rounded-sm border border-red-300"
                            >
                              <Trash2 size={12} /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4">
          <form
            onSubmit={saveProduct}
            className="bg-white rounded-sm w-full max-w-xl p-6 max-h-[90vh] overflow-auto"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {editing === "new" ? t.common.addProduct : t.common.edit}
              </h2>
              <button type="button" onClick={closeModal} className="p-1">
                <X size={18} />
              </button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Field label="Name (EN)" value={form.name_en} onChange={(v) => setForm({ ...form, name_en: v })} required testId="form-name-en" />
              <Field label="Name (TE)" value={form.name_te} onChange={(v) => setForm({ ...form, name_te: v })} required testId="form-name-te" />
              <Field label="Price / kg (base)" type="number" value={form.price_per_kg} onChange={(v) => setForm({ ...form, price_per_kg: v })} required testId="form-price" />
              <div>
                <label className="text-xs tracking-wider uppercase text-stone-500 font-semibold">Category</label>
                <select
                  data-testid="form-category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="mt-1 w-full bg-[#fafaf9] border border-stone-300 rounded-sm px-3 py-2 focus:outline-none focus:border-[#274e13]"
                >
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c} value={c}>{c.replace("_", " ")}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <Field
                  label="Pack sizes (kg, comma-separated)"
                  value={Array.isArray(form.pack_sizes) ? form.pack_sizes.join(", ") : form.pack_sizes}
                  onChange={(v) => setForm({ ...form, pack_sizes: v })}
                  testId="form-pack-sizes"
                />
                <div className="text-[11px] text-stone-500 mt-1">
                  Example: <span className="font-mono">5, 10, 26, 50</span>
                </div>
              </div>
              <div className="col-span-2">
                <Field
                  label="Bulk price tiers (pack_kg:price_per_kg, comma-separated)"
                  value={Array.isArray(form.price_tiers) ? form.price_tiers.map(t => `${t.pack_kg}:${t.price_per_kg}`).join(", ") : form.price_tiers}
                  onChange={(v) => setForm({ ...form, price_tiers: v })}
                  testId="form-price-tiers"
                />
                <div className="text-[11px] text-stone-500 mt-1">
                  Example: <span className="font-mono">5:60, 10:58, 26:54, 50:50</span> — bigger packs get cheaper /kg. Leave blank to use single base price.
                </div>
              </div>
              <Field
                label="Stock available (kg) — leave blank for unlimited"
                type="number"
                value={form.stock_kg ?? ""}
                onChange={(v) => setForm({ ...form, stock_kg: v })}
                testId="form-stock-kg"
              />
              <div className="col-span-2">
                <ImageUploadField
                  value={form.image_url}
                  onChange={(v) => setForm({ ...form, image_url: v })}
                />
              </div>
              <div className="col-span-2">
                <Field label="Description (EN)" value={form.description_en} onChange={(v) => setForm({ ...form, description_en: v })} testId="form-desc-en" />
              </div>
              <div className="col-span-2">
                <Field label="Description (TE)" value={form.description_te} onChange={(v) => setForm({ ...form, description_te: v })} testId="form-desc-te" />
              </div>
              <label className="col-span-2 flex items-center gap-2 text-sm p-3 bg-stone-50 rounded-sm border border-stone-200">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  data-testid="form-active"
                  className="w-4 h-4"
                />
                <span>
                  <span className="font-semibold">In stock</span>
                  <span className="text-stone-500 text-xs ml-2">
                    (Uncheck only if temporarily out of stock — product hides from storefront)
                  </span>
                </span>
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={closeModal} className="px-4 py-2 text-sm border border-stone-300 rounded-sm">
                {t.common.cancel}
              </button>
              <button
                type="submit"
                data-testid="save-product-btn"
                className="px-4 py-2 text-sm bg-[#274e13] hover:bg-[#38761d] text-white rounded-sm font-semibold"
              >
                {t.common.save}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <label className="text-xs tracking-wider uppercase text-stone-500 font-semibold">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="mt-1 w-full bg-[#fafaf9] border border-stone-300 rounded-sm px-3 py-2 focus:outline-none focus:border-[#274e13]"
      />
    </div>
  );
}

function ImageUploadField({ value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <label className="text-xs tracking-wider uppercase text-stone-500 font-semibold">
        Product Image
      </label>
      <div className="mt-1 flex gap-3 items-start">
        <div className="w-24 h-24 rounded-sm border border-stone-300 bg-stone-100 grid place-items-center overflow-hidden shrink-0">
          {value ? (
            <img
              src={resolveImageUrl(value)}
              alt="preview"
              className="w-full h-full object-cover"
              data-testid="form-image-preview"
            />
          ) : (
            <span className="text-[10px] text-stone-400 uppercase tracking-wider">No image</span>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <button
            type="button"
            data-testid="form-image-upload-btn"
            onClick={onPick}
            disabled={uploading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-stone-300 rounded-sm hover:bg-stone-50 disabled:opacity-60"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Uploading..." : value ? "Replace image" : "Upload image"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={onFile}
            className="hidden"
            data-testid="form-image-input"
          />
          <input
            data-testid="form-image-url"
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="or paste an image URL"
            className="w-full bg-[#fafaf9] border border-stone-300 rounded-sm px-3 py-2 text-xs focus:outline-none focus:border-[#274e13]"
          />
        </div>
      </div>
    </div>
  );
}

function OrderRow({ order, onUpdated }) {
  const [updating, setUpdating] = useState(false);
  const [copied, setCopied] = useState(false);

  const onStatusChange = async (status) => {
    setUpdating(true);
    try {
      await api.patch(`/orders/${order.id}/status`, null, { params: { status } });
      toast.success(`Order marked as ${STATUS_LABELS[status]}`);
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const deliveryLink = `${window.location.origin}/deliver/${order.id}?token=${order.delivery_token}`;
  const trackLink = `${window.location.origin}/track/${order.tracking_code}`;

  const copyDelivery = async () => {
    try {
      await navigator.clipboard.writeText(deliveryLink);
      setCopied(true);
      toast.success("Delivery link copied — share with driver");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <tr className="border-t border-stone-100" data-testid={`order-row-${order.id}`}>
      <td className="px-4 py-3">
        <a
          href={trackLink}
          target="_blank"
          rel="noreferrer"
          className="font-mono text-xs font-semibold text-[#274e13] hover:underline inline-flex items-center gap-1"
        >
          #{order.tracking_code} <ExternalLink size={11} />
        </a>
        <div className="text-xs text-stone-500 mt-1">{order.items.length} items</div>
      </td>
      <td className="px-4 py-3">
        <div className="font-semibold text-stone-900">{order.customer_name}</div>
        <div className="text-xs text-stone-500">{order.customer_phone}</div>
        <div className="text-xs text-stone-500 line-clamp-1 max-w-[200px]">
          {order.customer_address}
        </div>
      </td>
      <td className="px-4 py-3 font-semibold">₹{order.total.toFixed(0)}</td>
      <td className="px-4 py-3">
        <select
          data-testid={`order-status-${order.id}`}
          value={order.status}
          disabled={updating}
          onChange={(e) => onStatusChange(e.target.value)}
          className={`text-xs font-semibold px-2 py-1 rounded-sm border-0 focus:outline-none focus:ring-2 focus:ring-[#274e13]/30 ${
            STATUS_COLORS[order.status] || "bg-stone-100 text-stone-700"
          }`}
        >
          {ORDER_STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </td>
      <td className="px-4 py-3">
        <button
          data-testid={`copy-delivery-${order.id}`}
          onClick={copyDelivery}
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 border border-stone-300 rounded-sm hover:bg-stone-100"
          title="Copy delivery sharing link"
        >
          <Copy size={12} />
          {copied ? "Copied" : "Copy link"}
        </button>
      </td>
      <td className="px-4 py-3 text-stone-500 text-xs">
        {new Date(order.created_at).toLocaleString()}
      </td>
    </tr>
  );
}
